/**
 * AlertCentre - Main alert list with filtering and actions
 * Planner - Alert System
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAlerts } from '@/hooks/useAlerts'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  X,
  ChevronDown,
  RefreshCw,
  CheckCheck,
  Inbox
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

const SEVERITY_CONFIG = {
  critical: {
    icon: AlertTriangle,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-500',
    badgeVariant: 'destructive'
  },
  warning: {
    icon: AlertCircle,
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconColor: 'text-amber-500',
    badgeVariant: 'warning'
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-500',
    badgeVariant: 'secondary'
  }
}

const ALERT_TYPE_LABELS = {
  supply_shortfall: 'Supply Shortfall',
  batch_delay: 'Batch Delay',
  contamination: 'Contamination',
  spawn_shortage: 'Spawn Shortage',
  material_shortage: 'Material Shortage',
  task_overdue: 'Task Overdue',
  system: 'System'
}

function AlertItem({ alert, onMarkRead, onDismiss }) {
  const [isOpen, setIsOpen] = useState(false)
  const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info
  const Icon = config.icon

  const timeAgo = formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })

  return (
    <Card className={cn(
      'transition-all',
      config.bgColor,
      config.borderColor,
      !alert.read && 'ring-2 ring-offset-2',
      alert.severity === 'critical' && !alert.read && 'ring-red-500',
      alert.severity === 'warning' && !alert.read && 'ring-amber-500'
    )}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="p-4">
          <div className="flex items-start gap-3">
            <Icon className={cn('w-5 h-5 mt-0.5 flex-shrink-0', config.iconColor)} />

            <div className="flex-1 min-w-0">
              <CollapsibleTrigger className="flex items-start justify-between w-full text-left group">
                <div className="space-y-1 pr-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={cn(
                      'font-medium',
                      !alert.read && 'font-semibold'
                    )}>
                      {alert.title}
                    </h3>
                    <Badge variant={config.badgeVariant} className="text-xs">
                      {alert.severity}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{timeAgo}</p>
                </div>
                <ChevronDown className={cn(
                  'w-5 h-5 text-muted-foreground transition-transform flex-shrink-0',
                  isOpen && 'rotate-180'
                )} />
              </CollapsibleTrigger>
            </div>
          </div>

          <CollapsibleContent>
            <div className="mt-4 pt-4 border-t space-y-4">
              {alert.message && (
                <p className="text-sm">{alert.message}</p>
              )}

              {/* Context Links */}
              <div className="flex flex-wrap gap-2 text-sm">
                {alert.batch && (
                  <Link
                    to={`/production/batches/${alert.batch.id}`}
                    className="text-primary hover:underline"
                  >
                    Batch: {alert.batch.batch_code}
                  </Link>
                )}
                {alert.strain && (
                  <Link
                    to={`/production/strains/${alert.strain.id}`}
                    className="text-primary hover:underline"
                  >
                    Strain: {alert.strain.name}
                  </Link>
                )}
                {alert.week_start && (
                  <span className="text-muted-foreground">
                    Week: {alert.week_start}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {!alert.read && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onMarkRead(alert.id)}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Mark as Read
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDismiss(alert.id)}
                >
                  <X className="w-4 h-4 mr-1" />
                  Dismiss
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </Card>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Inbox className="w-12 h-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium">No alerts</h3>
      <p className="text-sm text-muted-foreground mt-1">
        All caught up! No alerts to display.
      </p>
    </div>
  )
}

export default function AlertCentre() {
  const {
    alerts,
    loading,
    error,
    unreadCount,
    refetch,
    markAsRead,
    markAllAsRead,
    dismissAlert
  } = useAlerts()

  const [severityFilter, setSeverityFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')

  // Filter alerts
  const filteredAlerts = alerts.filter(alert => {
    if (severityFilter !== 'all' && alert.severity !== severityFilter) return false
    if (typeFilter !== 'all' && alert.alert_type !== typeFilter) return false
    return true
  })

  // Group by severity for display
  const criticalAlerts = filteredAlerts.filter(a => a.severity === 'critical')
  const warningAlerts = filteredAlerts.filter(a => a.severity === 'warning')
  const infoAlerts = filteredAlerts.filter(a => a.severity === 'info')

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Error loading alerts: {error}</p>
        <Button variant="outline" size="sm" onClick={refetch} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(ALERT_TYPE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark All Read
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={refetch} disabled={loading}>
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Alert List */}
      {loading && alerts.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : filteredAlerts.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-6">
          {/* Critical Alerts */}
          {criticalAlerts.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-red-600 uppercase tracking-wide">
                Critical ({criticalAlerts.length})
              </h2>
              {criticalAlerts.map(alert => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  onMarkRead={markAsRead}
                  onDismiss={dismissAlert}
                />
              ))}
            </div>
          )}

          {/* Warning Alerts */}
          {warningAlerts.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-amber-600 uppercase tracking-wide">
                Warnings ({warningAlerts.length})
              </h2>
              {warningAlerts.map(alert => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  onMarkRead={markAsRead}
                  onDismiss={dismissAlert}
                />
              ))}
            </div>
          )}

          {/* Info Alerts */}
          {infoAlerts.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                Info ({infoAlerts.length})
              </h2>
              {infoAlerts.map(alert => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  onMarkRead={markAsRead}
                  onDismiss={dismissAlert}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
